import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencilAlt, faTrash, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAdminBlogs, deleteAdminBlog } from '../../../store/slices/blogAdminSlice';

const BlogManagement = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    
    // Select state from Redux
    const { blogs, loading } = useAppSelector((state) => state.blogAdmin);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [blogToDelete, setBlogToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Fetch blogs on component mount
    useEffect(() => {
        dispatch(fetchAdminBlogs({ page: 1, limit: 100 })); // Assuming we want a larger limit for admin list
    }, [dispatch]);

    const handleEdit = (blog) => {
        const identifier = blog.page_url || blog.id;
        navigate(`/admin/blogs/${identifier}/edit`);
    };

    const handleDeleteClick = (id) => {
        setBlogToDelete(id);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!blogToDelete) return;
        
        setIsDeleting(true);
        try {
            await dispatch(deleteAdminBlog(blogToDelete)).unwrap();
            toast.success('Blog deleted successfully');
        } catch (error) {
            const errorMessage = typeof error === 'string' ? error : (error?.message || 'Failed to delete blog');
            toast.error(errorMessage);
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setBlogToDelete(null);
        }
    };

    // Helper function to extract text from HTML content and truncate
    const stripHtmlAndTruncate = (html, maxLength = 120) => {
        if (!html) return '';
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        const text = tmp.textContent || tmp.innerText || '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Helper to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown Date';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    return (
        <div className="p-6 bg-[#ecf0f5] min-h-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-2xl font-bold text-gray-800">Blogs</h1>
                <button 
                    onClick={() => navigate('/admin/blogs/add')}
                    className="flex items-center gap-2 bg-[#ffd200] hover:bg-[#e6bd00] text-black px-6 py-2.5 rounded shadow-sm font-bold transition-all duration-200"
                >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Add Blogs</span>
                </button>
            </div>

            {/* Loading State */}
            {loading && blogs.length === 0 ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : blogs.length === 0 ? (
                 <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                    No blogs found. Click "Add Blogs" to create one.
                </div>
            ) : (
                /* White Container with Blogs Grid */
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {blogs.map((blog) => (
                            <div 
                                key={blog.id} 
                                className="bg-white rounded-[10px] p-5 flex flex-col overflow-hidden"
                                style={{ boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)' }}
                            >
                                {/* Action Buttons - Top Right */}
                                <div className="flex justify-end gap-2 mb-3">
                                    <button
                                        onClick={() => handleEdit(blog)}
                                        className="w-[24px] h-[24px] flex items-center justify-center bg-[#ffd200] text-black rounded-[4px] hover:bg-[#e6bd00] transition-colors shadow-sm border-none cursor-pointer"
                                        title="Edit Blog"
                                    >
                                        <FontAwesomeIcon icon={faPencilAlt} className="text-base" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(blog.id)}
                                        className="w-[24px] h-[24px] flex items-center justify-center bg-[#e74c3c] text-white rounded-[4px] hover:bg-[#c0392b] transition-colors shadow-sm border-none cursor-pointer"
                                        title="Delete Blog"
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="text-base" />
                                    </button>
                                </div>

                                {/* Image Container */}
                                <div className="relative h-56 overflow-hidden bg-gray-100 mb-4">
                                    {blog.image_url || blog.image ? (
                                        <img
                                            src={blog.image_url || `/admin/storage/blogs/${blog.image}`}
                                            alt={blog.title || blog.page_name}
                                            onError={(e) => {
                                                e.target.onerror = null; 
                                                e.target.src = '/hero-image.jpeg'; // Fallback
                                            }}
                                            className="w-full h-full object-contain"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                            <span className="text-gray-400">No Image</span>
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="flex flex-col flex-1">
                                    <h3 className="text-lg font-bold text-gray-800 mb-2 leading-snug line-clamp-2">
                                        {blog.title || blog.page_name}
                                    </h3>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-3 line-clamp-2">
                                        {blog.summary || blog.text_content || ''}
                                    </p>
                                    
                                    <div className="mt-auto">
                                        <p className="text-gray-500 text-sm mb-2">
                                            Author: {blog.created_by || blog.author || 'Unknown'} {blog.published_date || blog.created_at ? `on ${formatDate(blog.published_date || blog.created_at)}` : ''}
                                        </p>
                                        <button
                                            onClick={() => navigate(`/admin/blogs/${blog.page_url}`)}
                                            className="text-[#f39c12] hover:text-[#d35400] font-semibold text-sm flex items-center gap-2 transition-colors no-underline bg-transparent border-none cursor-pointer p-0"
                                        >
                                            Read More <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <UniversalConfirmModal
                isOpen={showDeleteModal}
                onClose={() => !isDeleting && setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                type="delete"
                title="Are you sure?"
                message='You are about to DELETE this Blog. Click DELETE to proceed or CANCEL to exit.'
                confirmText={isDeleting ? 'DELETING...' : 'DELETE'}
                cancelText="CANCEL"
                theme="modern"
            />
        </div>
    );
};

export default BlogManagement;
